import { FC, useMemo, useState } from "react"
import { useQueryClient } from "react-query"
import { Formik, Form, FormikValues, getIn } from "formik"
import * as Yup from 'yup'

import { 
  FormikFileUpload,
  FormikSelect,
  FormikDateSelector,
  StatusErrorMessage, 
  FormikNotesArea
} from "../../../../../components/widgets"

import { doFormSubmit, processNoteValues } from "../../../../../components/helpers"
import { useUserLoader } from "../../../../core/users"

import { cloneDeep, pickBy } from "lodash"
import { GenerateInspectionROIModal } from "../components/GenerateInspectionROIModal"
import { SubmitButtonRow } from "../../../../../components/layout"
import { DLMCreateROIButton } from "../../widgets/DLMCreateROIButton"

const initInspectionSetupValues = {
  inspectionDate: '',
  inspectorID: 0,
  roiModels: [],
  note: '',
  noteAttachments: [],    
}

const schema = Yup.object({
  inspectionDate: Yup.string().trim().label('Inspection Date').required(),
  inspectorID: Yup.number().integer().label('Inspector').required().moreThan(0,'Select Site Inspector'),
  roiModels: Yup.array().of(
    Yup.object().shape({
      roiId: Yup.number().integer().label('ROI Id'),
      roiAttachmentId: Yup.number().integer().moreThan(0,'ROI required').required().label('ROI Attachment'),
      roiAttachmentIdAttachmentName: Yup.string().trim(),
    })
  ).required().min(1, 'Create ROI'),  
  note: Yup.string().trim().label('Note'),  
  noteAttachments: Yup.array(),    
})

type Props = {
  id: number
  appID: number
  doClose: Function
}

const InspectionSetupForm: FC<Props> = ({id, appID, doClose}) => {
  const queryClient = useQueryClient()

  const [roiModel, setROIModel] = useState<any>(null);
  const [roiIndex, setROIIndex] = useState(-1);
  const [show_genroi, showGenROI] = useState(false);

  const { data: inspectors } = useUserLoader({userType: 'DLM Inspector'});

      
  const initValues = useMemo(() => {
    return cloneDeep(initInspectionSetupValues);
  }, []);
    
  const closeROIModal = () => {
    showGenROI(false);
  }

  const doSubmit = async (values: any, actions: FormikValues) => {        

    const { setStatus, setSubmitting } = actions;
    const _models = values.roiModels.map( (model: any) => pickBy(model, (value, key) => (value && (! key.match(/AttachmentName$/i)))));

    const payload = { 
      ...pickBy(values, (value, key) => (value && (! key.match(/(note|AttachmentName|roiModels)$/i)))),
      siteApplicationid: id, 
      roiModels: _models
    };
    
    processNoteValues(values, payload);

    await doFormSubmit('DLMPhase_3_1_2_1Servlet', payload, setStatus, setSubmitting, () => {
      queryClient.invalidateQueries(['DLMAPPLICATIONS-LIST', { lastpath: 'label-applications-list'}]);
      queryClient.invalidateQueries(['DLMAPPLICATIONS-LIST', { listmode: 'own'} ]);
      queryClient.invalidateQueries(['DLMAPPLICATION-LOAD', { dlmappid: appID } ]);
      doClose();                            
    });          
  }

  return(
  <>
  <Formik
    initialValues={initValues}
    validationSchema={schema}
    enableReinitialize={true}
    onSubmit={doSubmit}
  >
  {({status, setFieldValue,values, validateField}) => {

    const roiModels = getIn(values, 'roiModels');
    
    const saveROIValues = (values: any) => {
      if (! (values && typeof(values) === 'object')) return;

      setFieldValue(`roiModels.${roiIndex}`, {
        ...roiModels[roiIndex],
        ...values,
        roiAttachmentId : 0,
        roiAttachmentIdAttachmentName: ''
      }, true);

      validateField('roiModels');
    }
    
    return (
    <>
    <Form id='kt_doclabel_inspection_setup_form' className='form' noValidate>
      <div className='d-flex flex-column p-2' >
        <div className="row mb-4">        
          <FormikSelect size="6" label="Assign Inspector" name="inspectorID" required={true} options={inspectors} placeholder='Select Inspector' />
          <FormikDateSelector size="6" label="Inspection Date" name="inspectionDate" required={true} showShiftSelector={true} dateFormat='LL' />
        </div>  

        <div className="d-flex flex-row justify-content-between align-items-center my-2">
          <div className="fs-6 fw-bold">Upload ROI</div>
          {(!roiModels?.length) && 
            <DLMCreateROIButton siteID={id} />                  
          }
        </div>    
        { (roiModels?.length > 0) && 
        <>
        <div className='separator border-gray-200 mb-2'></div>
        <div className="mh-250px overflow-auto pe-4">        
          {roiModels.map((roiModel: any, idx: number) => (
          <div key={`setROIrow_${idx}`} className="row mb-4">
            <FormikFileUpload 
              size="9" 
              label={`[${roiModel?.cbRef ? roiModel.cbRef : `${roiModel.series}${roiModel.no}${roiModel?.split ? roiModel?.split :''}`}] ${roiModel.projectName}`}
              name={`roiModels.${idx}.roiAttachmentId`} 
              acceptFiles="UserFileFormats" 
              required={true}   
              disabled={getIn(values, `roiModels.${idx}.roiId`) ? false : true} 
              subTitle={getIn(values, `roiModels.${idx}.roiId`) ? "Upload ROI here" : "Generate ROI first."}     
              onUpdateFunc={ () => {
                validateField('roiModels');
              }}                   
            />
            <div className="col-3">
              <button
                type="button"
                className='btn btn-sm btn-light-secondary py-1 mb-1 px-3'
                onClick={() => {
                  setROIModel(roiModel);
                  setROIIndex(idx);
                  showGenROI(true);
                }}        
              >
                <span className='indicator-label'>Generate ROI</span>        
              </button>
            </div>              
          </div>
          ))        
        }
        </div>            
        </>
        }        
        <FormikNotesArea />
        <StatusErrorMessage status={status} />
        <SubmitButtonRow doClose={doClose} />
      </div>
    </Form>
    { (show_genroi && roiModel) && 
    <GenerateInspectionROIModal baseROI={roiModel} onROIGenerated={saveROIValues} doClose={closeROIModal} />
    } 
    </>
    )   
  }}
  </Formik>
  </>
  )
}

export { InspectionSetupForm }